/* ROOT STATE */

import { Session, SessionStatus } from './session';
import { StageType, Stage, Pointer, StageDBStates, TextShout, Video } from './stageState';
import { Element } from './element';
import { LoggedInUser, User } from '.';

export interface SocketState {
  connected: boolean;
}

/* ACTIONS */

export enum SocketActionsKeys {
  CONNECT_SOCKET = 'CONNECT_SOCKET',
  DISCONNECT_SOCKET = 'DISCONNECT_SOCKET',
  SOCKET_ON_CONNECT = 'SOCKET_ON_CONNECT',
  SOCKET_EMIT = 'SOCKET_EMIT',
  RESET_STATE = 'RESET_STATE',
}

export interface SocketActionsPayload {
  [SocketActionsKeys.CONNECT_SOCKET]: void;
  [SocketActionsKeys.DISCONNECT_SOCKET]: void;
  [SocketActionsKeys.SOCKET_ON_CONNECT]: void;
  [SocketActionsKeys.SOCKET_EMIT]: { type: string; params: unknown[] };
  [SocketActionsKeys.RESET_STATE]: void;
}

/* MUTATIONS */

export enum SocketMutationsKeys {
  SOCKET_CONNECTED = 'SOCKET_CONNECTED',
  SOCKET_EMITTED = 'SOCKET_EMITTED',
  SOCKET_MSG = 'SOCKET_MSG',
  SOCKET_REGISTER_LISTENER = 'SOCKET_REGISTER_LISTENER',
  SOCKET_RESET_STATE = 'SOCKET_RESET_STATE',
}

export interface SocketMutationsPayload {
  [SocketMutationsKeys.SOCKET_CONNECTED]: void;
  [SocketMutationsKeys.SOCKET_EMITTED]: void;
  [SocketMutationsKeys.SOCKET_MSG]: void;
  [SocketMutationsKeys.SOCKET_REGISTER_LISTENER]: { key: string; fn: (params: unknown) => void };
  [SocketMutationsKeys.SOCKET_RESET_STATE]: void;
}

/* GETTERS */

export enum SocketGettersKeys {
  test = 'test',
}

export interface SocketGettersReturns {
  test: boolean;
}

/* SOCKET EMIT TYPES */

export interface CreateSessionEmit {
  type: 'create_session';
  params: [(param: CreateSessionResponse) => void];
}

export interface JoinSessionEmit {
  type: 'join_session';
  params: [{ pin: Session['pin'] }];
}

export interface LeaveSessionEmit {
  type: 'leave_session';
  params: [{ pin: Session['pin'] }];
}

export interface StartSessionEmit {
  type: 'start_session';
  params: [{ pin: Session['pin'] }];
}

export interface PauseSessionEmit {
  type: 'pause_session';
  params: [{ pin: Session['pin'] }];
}
export interface DeleteSessionEmit {
  type: 'delete_session';
  params: [{ pin: Session['pin'] }];
}

export interface StartConferenceEmit {
  type: 'start_conference';
  params: [{ pin: Session['pin'] }];
}
export interface StopConferenceEmit {
  type: 'stop_conference';
  params: [{ pin: Session['pin'] }];
}
export interface JoinConferenceEmit {
  type: 'join_conference';
  params: [{ pin: Session['pin'] }];
}
export interface LeaveConferenceEmit {
  type: 'leave_conference';
  params: [{ pin: Session['pin'] }];
}
export interface StartExternalConferenceEmit {
  type: 'start_ext_conference';
  params: [{ pin: Session['pin'] }];
}

export interface CreateElementEmit {
  type: 'create_element';
  params: [{ board_element: Element; board_id: number }];
}

export interface UpdateElementEmit {
  type: 'update_element';
  params: [{ board_element: Element; board_id: number; save: boolean }];
}

export interface StageUpdateEmit {
  type: StageType.Stage;
  params: [Stage & { board_id: number }];
}
export interface FocusUpdateEmit {
  type: StageType.Focus;
  params: [{ board_id: number; board_element: Element }];
}

export interface PointerUpdateEmit {
  type: StageType.Pointer;
  params: [Pointer & { board_id: number; user: number }];
}

export interface ShoutEmit {
  type: StageType.Shout;
  params: [Omit<TextShout & { board_id: number }, 'user'>];
}
export interface VideoStateEmit {
  type: StageType.Video;
  params: [Video];
}

export interface SessionUsersEmit {
  type: 'list_session_users';
  params: [{ pin: Session['pin'] }];
}

export interface PointerOfferEmit {
  type: 'offer_laser';
  params: [{ guestOrUser: LoggedInUser }];
}

export interface PointerOfferAcceptEmit {
  type: 'accept_laser';
  params: [{ guestOrUser: LoggedInUser; key: string }];
}

export interface PointerOfferRejectEmit {
  type: 'reject_laser';
  params: [{ guestOrUser: LoggedInUser; key: string }];
}

export interface PointerOfferReclaimEmit {
  type: 'reclaim_laser';
  params: [{ guestOrUser: LoggedInUser }];
}
/* SOCKET RESPONSE TYPES */

export enum responseCode {
  SUCCESS,
  ERROR,
  PERMISSION_DENIED,
}

type CreateSessionResponse = { pin: string; code: responseCode };
export type JoinSessionResponse = {
  code: responseCode;
  status: SessionStatus;
  conference_ongoing: boolean;
  states: StageDBStates;
  users: LoggedInUser[];
};
export type SessionStartedResponse = {
  status: SessionStatus;
  session_pin: string;
  states: StageDBStates;
};

export type SessionUsersResponse = {
  users: User[];
};

export type elementResponse = { code: responseCode; board_element: Element };
